<template>
  <b-modal :active.sync="isModalActive" :width="520" scroll="keep">
    <div class="p-4 bg-white border rounded shadow-lg border-blue-light">

      <header class="w-full text-xl font-semibold uppercase text-blue-light">
        {{ $t(quality ? "validate_quality" : "validate_progress") }}
      </header>

      <div class="w-full">
        <form @submit.prevent="submitForm" class="w-full">
          <div>

            <div class="flex flex-row items-center mt-4">
              <div class="w-3 h-3 mr-1 rounded-full bg-green"></div>
              <div class="font-semibold text-black text-lg">{{ data.name }}</div>
            </div>

            <div class="flex flex-row items-center justify-start mt-1" v-if="data.unit">
              <div class="p-2 text-lg font-semibold text-white rounded-full bg-blue ">{{ formatFigure(data.executed_quantity) }} {{ data.unit.name }} {{ $t('executed') }}</div>
              <div class="p-2 text-lg font-semibold text-white rounded-full bg-orange ">{{ formatFigure(pendingQuantity) }} {{ data.unit.name }} {{ $t(quality ? 'for_validate_quality' : 'for_validate') }}</div>
              <div class="ml-1 text-xs font-medium text-grey-dark">{{ formatFigure(form.total - data.executed_quantity) }} {{ data.unit.name }} {{ $t('pendings') }}</div>
            </div>
            <div v-if="isTask()">
               <div class="p-2 text-lg font-semibold text-white rounded-full bg-blue">
                {{ $t('pending') }}: {{assignmentLine ? assignmentLine.executed_quantity : data.pending_validation_quantity}} %
               </div>
            </div>

            <!-- Trazabilidad en modo calidad: avances pendientes del doble check y quién validó la inspección -->
            <div v-if="quality && qualityPendingLines.length" class="mt-3">
              <div v-for="line in qualityPendingLines" :key="'ql_'+line.id" class="text-xs text-grey-dark">
                {{ formatFigure(line.executed_quantity) }} {{ data.unit ? data.unit.name : '' }}
                · {{ lineUserName(line) }}
                · {{ formatLineDate(line.end_date) }}
              </div>
              <div v-for="action in inspectionValidations" :key="'qv_'+action.id" class="text-xs font-medium text-blue mt-1">
                {{ lineUserName(action) }} {{ $t('validated_action') }} {{ formatFigure(action.executed_quantity) }} {{ data.unit ? data.unit.name : '' }} · {{ formatLineDate(action.created_at) }}
              </div>
            </div>


            <div class="flex flex-row justify-between w-full mt-4">

              <div class="flex flex-col w-1/3">
              </div>

              <div class="flex flex-col w-1/3 mx-4 ">
              </div>

              <div class="flex flex-col w-1/3">
              </div>

            </div>


            <!-- Input de cantidad a validar -->
            <div class="w-full mt-3" v-if="isResource()" >
              <div class="text-xs font-medium text-blue">{{ $t("amount_to_validate") }}</div>
              <b-input
                v-model="form.executed_quantity"
                name="executed_quantity"
                class="w-full"
                type="number"
              ></b-input>
              <div v-if="exceedsPending" class="mt-1 text-xs text-red">
                {{ $t("amount_exceeds_pending") }}
              </div>
            </div>


            <!-- Observación sobre la validacion -->
            <div class="w-full pb-3 mt-2" v-if="isResource() || isTask()" >
              <div class="text-xs font-medium text-blue">{{ $t("observations") }}</div>
              <b-input
                v-model="form.observation"
                name="observation"
                :placeholder="$t('observations')"
                :disabled="false"
                class="w-full"
                maxlength="2000"
                type="textarea"
              ></b-input>
              <div v-if="isLoading" class="my-4 text-center">
                <scale-loader />
              </div>

            </div>
          </div>

          <div class="inline-flex justify-end w-full pb-3">

            <button class="btn btn-blue mr-2" type="button" @click="submitStatus(3)" :disabled="isLoading || exceedsPending">
              <b-icon pack="fas" icon="thumbs-up" size="is-small"/>&nbsp;
              {{ $t("validate") }}
            </button>

            <button class="btn btn-red" type="button" @click="submitStatus(4)" :disabled="isLoading || exceedsPending">
              <b-icon pack="fas" icon="thumbs-down" size="is-small"/>&nbsp;
              {{ $t("reject") }}
            </button>

          </div>
        </form>
      </div>
    </div>
  </b-modal>
</template>

<script>
// import Vue from 'vue';
import axios from "axios";
import moment from "moment";
import { mapGetters } from "vuex";
import { formatProjectFigure } from "·/helpers/quantity-display";
import EventBus from "~/plugins/bus";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";

export default {
  name: "assignment-contribute-modal",

  components: {
    ScaleLoader,
  },

  props: {
    entityId: { type: Number, required: true },
    entityType: { type: String, required: true },
  },

  data: () => ({
    cancelRequest: null,
    uploadPercentage: 0,
    isModalActive: false,
    id: null,
    isLoading: false,
    data: {},
    form: {
      executed_quantity: 0, // cantidad a confirmar/rechazar
      observation: null,
      status: null, // estado 3 aceptar o 4 rechazar
      total: 0,
    },

    assignmentLine: null,

    // Modo doble check de calidad: valida/rechaza cantidades ya validadas por inspección
    quality: false,

  }),

  computed: {
    ...mapGetters({
      company: "app/company",
      project: "app/project",
    }),
    pendingQuantity() {
      const pending = this.quality
        ? this.data.pending_quality_validation_quantity
        : this.data.pending_validation_quantity;
      return pending || 0;
    },

    // Solaring: bloquear validar mas cantidad que la pendiente
    exceedsPending() {
      if (!this.isResource()) return false;
      if (!this.company || this.company.alias !== 'solaring') return false;
      const value = parseFloat(this.form.executed_quantity);
      if (isNaN(value)) return false;
      return value > this.pendingQuantity;
    },

    // líneas validadas por inspección pendientes del doble check de calidad
    qualityPendingLines() {
      if (!this.quality || !Array.isArray(this.data.lines)) return [];
      return this.data.lines.filter((l) => Number(l.status) === 0 && Number(l.quality_status) === 1);
    },

    // acciones de validación de inspección (3=validó, 5=parcial) que corresponden a lo pendiente de calidad:
    // pueden ser varias y de distintos validadores (cada lote validado genera una acción)
    inspectionValidations() {
      if (!this.quality || !Array.isArray(this.data.lines) || !this.qualityPendingLines.length) return [];
      // las lineas pendientes se marcaron al validarse (updated_at); solo interesan las acciones desde el lote más antiguo
      const oldestPending = Math.min(...this.qualityPendingLines.map((l) => new Date(l.updated_at).getTime()));
      return this.data.lines
        .filter((l) => [3, 5].includes(Number(l.status)))
        .filter((l) => new Date(l.created_at).getTime() >= oldestPending - 60000)
        .sort((a, b) => b.id - a.id);
    },
  },

  methods: {
    formatFigure(value) {
      const number = Number(value)
      if (!Number.isFinite(number) || number === 0) {
        return '0'
      }
      return formatProjectFigure(number, this.project)
    },



    onAddClicked() {
      this.$refs.modal.show();
    },


    submitStatus(status) {
      this.form.status = status
      this.submitForm()
    },


    /**
     * Inserta una linea tipo "aceptadas/rechazadas las cantidades pendientes hasta ahora" (executed_quantity especifica cuanto acepta/rechaza)
     */
    async submitForm() {
      // en modo calidad sin cantidad pendiente (datos obsoletos o doble click) no hay nada que validar
      if (this.quality && this.pendingQuantity <= 0) {
        this.$toast.open({
          message: this.$t("incorrect_amount_to_validate"),
          type: "is-danger",
          position: "is-top-right",
        });
        this.hide();
        return;
      }
      let executed_quantity = this.assignmentLine ? this.assignmentLine.executed_quantity : this.pendingQuantity;
      if (this.isResource() && this.form.executed_quantity) {
        executed_quantity = parseFloat(this.form.executed_quantity);
        if (isNaN(executed_quantity) || executed_quantity > this.pendingQuantity) {
          this.$toast.open({
            message: this.$t("incorrect_amount_to_validate"),
            type: "is-danger",
            position: "is-top-right",
          });
          return;
        }
      }

      let data = {
        executed_quantity,
        observation: this.form.observation || null,
        status: this.form.status,
        line_id: this.assignmentLine ? this.assignmentLine.id : null,
      };

      let url =
        "/api/v2/assignments/" + this.id + (this.quality ? "/validate-quality" : "/validate-resource");

      axios({
        method: "post",
        url: url,
        data: data,
      })
        .then((response) => {
          this.$toast.open({
            message: response.data.error || response.data.msg || this.$t("assignment_update_success"),
            type: response.data.error || response.data.msg ? "is-danger" : "is-success",
            position: "is-top-right",
          });

          this.$emit("reload", { id: this.id, entityId: this.entityId }); //pide a su componente padre que recargue
          EventBus.$emit("refreshActivityDocumentsNotifications", true);
        })
        .catch((error) => {
          //Se produjo un error
          this.isLoading = false;
          if (error.response && error.response.status === 422) {
            //extraemos el primer error de validación
            error.error_msg =
              error.response.data.errors[
                Object.keys(error.response.data.errors)[0]
              ][0];
          }

          // Mostramos el error
          this.$toast.open({
            message: error.error_msg || this.$t("assignment_update_error"),
            type: "is-danger",
            position: "is-top-right",
          });
        });

      this.hide();
      this.$emit("reload");
    },

    formatLineDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },

    lineUserName(line) {
      if (!line || !line.user) return "";
      return line.user.basic ? line.user.basic.fullname : line.user.name;
    },

    /**
     * Comprueba si los datos son de tipo recurso
     */
    isResource() {
      return this.data.type === "App\\Models\\AssignmentType\\Resource";
    },

    /**
     * Comprueba si los datos son de tipo tarea
     */
    isTask() {
      return this.data.type === "App\\Models\\AssignmentType\\Task";
    },


    /**
     * Muestra este modal
     */
    show(assignment_id, assignmentLine = null, quality = false) {
      this.id = assignment_id;
      this.assignmentLine = assignmentLine;
      this.quality = quality;
      this.getAssignmentData();
      this.isModalActive = true;
    },

    /**
     * Toma los datos de esta medición de la API
     */
    getAssignmentData() {
      var self = this;
      const { data } = axios
        // en modo calidad pedimos las lineas para mostrar la trazabilidad (quién registró y quién validó)
        .get("/api/v2/assignment/" + this.id + (this.quality ? "?withLines=1" : ""))
        .then((response) => {

          if (response.data && response.data.success) {
            self.data = response.data.assignment;
            self.form = {
              observation: null,
              total: self.data.total_quantity != null ? self.data.total_quantity : self.data.partial,
              executed_quantity: self.assignmentLine ? self.assignmentLine.executed_quantity : self.pendingQuantity
            };

          } else {
            this.$notify.error(response.data.msg || "assignment_loading_error");
          }
        });
    },


    /**
     * valores por defecto del formulario
     */
    defaultFormValues() {
      return {
        executed_quantity: 0,
        observation: null,
        status: null,
        total: 0,
      };
    },

    /**
     * Oculta este modal
     */
    hide() {
      this.id = null;
      this.data = [];
      this.quality = false;
      this.isModalActive = false;
      this.form = this.defaultFormValues();
    },

  },
};
</script>
<style scoped>
/* Oculta los botones para aumentar los inputs numéricos */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

input[type="number"] {
  -moz-appearance: textfield; /* Firefox */
}
</style>
